/** Vowed iPhone Gate PWA — DOM wiring + the request poll loop. */
import {
  enroll,
  decide,
  isEnrolled,
  enablePush,
  syncPushSubscription,
  isUnlockRequest,
  isEnrollAttestRequest,
  isMintRequest,
  mintCard,
  getHistory,
  captureEnrollmentParams,
  applyEnrollmentInput,
  vaultId,
  withVault,
  hasFaceId,
  setUpFaceIdOnThisPhone,
  notApprovedReason,
  DEFAULT_RELAY,
  type PendingGrant,
  type HistoryEntry,
} from "./app.js";
import { enrollFingerprint } from "./gate-sign.js";
import type { FaceIdFailure, SetupFailure } from "./faceid.js";
import { installUpdater } from "./update.js";

/** The request-card status when Face ID did not pass and nothing was signed (J-3529). */
export function notApprovedMessage(reason: FaceIdFailure): string {
  return `Not approved — ${notApprovedReason(reason)}`;
}

/** The Set up Face ID status when no new passkey was pinned (J-3529, SEC-CB-021). */
export function faceIdSetupMessage(reason: SetupFailure, hadPasskey: boolean): string {
  const why =
    reason === "cancelled"
      ? "✗ Face ID setup was cancelled."
      : reason === "not-verified"
        ? "✗ This passkey provider did not verify Face ID — choose Apple Passwords and try again."
        : "✗ Face ID setup did not finish (Face ID needs Safari or the installed Home-Screen app).";
  return why + (hadPasskey ? " Your existing Face ID passkey is unchanged." : " Approvals stay blocked until it is set up.");
}

/**
 * Pure view-model for a pending request — the unlock-vs-grant branch decision, made testable
 * without a DOM. `kind` drives which card the UI shows; `title` is the operator-facing prompt.
 */
export interface RequestView {
  kind: "unlock" | "grant" | "enroll" | "mint";
  title: string;
  scope: string;
}
export function requestView(req: PendingGrant): RequestView {
  const machine = req.target?.machine ?? req.target?.daemon_id ?? "(unknown machine)";
  if (isMintRequest(req)) {
    // D-028a: a STANDING service-token mint — never rendered as a grant or an unlock.
    return {
      kind: "mint",
      title: `Mint a standing service token for "${req.descriptor.service_id ?? "(unknown service)"}"?`,
      scope: `${req.descriptor.wallets?.join(", ")} · ${req.descriptor.ops?.join(", ")}`,
    };
  }
  if (isEnrollAttestRequest(req)) {
    // R1 companion (D-073): a new-device enrollment attestation. The fingerprint the operator must
    // confirm is shown (recomputed) in the Face-ID prompt at approve time — not trusted here.
    return {
      kind: "enroll",
      title: `Approve new device "${req.descriptor.friendly_name ?? req.descriptor.gate_id ?? "(unnamed)"}"?`,
      scope: "New gate device — its fingerprint must match the new phone",
    };
  }
  if (isUnlockRequest(req)) {
    return { kind: "unlock", title: `Unlock ${machine}?`, scope: "Unlock the vault on this Mac" };
  }
  return {
    kind: "grant",
    title: `Grant ${req.descriptor.wallets.join(", ")}?`,
    scope: `${req.descriptor.wallets.join(", ")} · ${req.descriptor.ops.join(", ")}`,
  };
}

/**
 * Pure view-model for one history row — the read-only render decision, made testable without a
 * DOM or IndexedDB. `icon` distinguishes unlock vs grant; `outcome` shows ✓/✗; `when` is a
 * locale time string. Every field comes straight from the stored HistoryEntry (no invention).
 */
export interface HistoryRow {
  icon: string;
  machine: string;
  scope: string;
  reason: string;
  outcome: string;
  when: string;
}
export function historyRow(e: HistoryEntry): HistoryRow {
  return {
    icon: e.kind === "unlock" ? "🔓" : e.kind === "enroll" ? "➕" : e.kind === "mint" ? "🪪" : "🔑",
    machine: e.machine,
    scope: e.scope,
    reason: e.reason,
    outcome: e.outcome === "approved" ? "✓ approved" : "✗ denied",
    when: new Date(e.ts).toLocaleString(),
  };
}

const $ = (id: string) => document.getElementById(id)!;
const show = (id: string, on: boolean) => ($(id).style.display = on ? "" : "none");
const setText = (id: string, t: string) => ($(id).textContent = t);

/** Reflect whether this app knows its vault-id — the enroll precondition the relay enforces. */
function reflectVaultLink(): void {
  setText(
    "link-status",
    vaultId()
      ? "✓ Vault linked."
      : "Not linked yet — paste the enrollment link your Mac prints, then tap Use enrollment link.",
  );
}

async function refresh(): Promise<void> {
  const relay = localStorage.getItem("vowed-relay-url") || DEFAULT_RELAY;
  (($("relay") as HTMLInputElement)).value = relay;
  reflectVaultLink();
  void reflectFaceId();
  const enrolled = await isEnrolled();
  show("setup", !enrolled);
  show("gate", enrolled);
  if (enrolled) {
    poll();
    reconcilePush();
    renderHistory();
  }
}

/** Render the read-only request-history list from local IndexedDB (newest-first). */
async function renderHistory(): Promise<void> {
  const list = await getHistory().catch(() => [] as HistoryEntry[]);
  const ul = $("history-list");
  show("history", list.length > 0);
  ul.replaceChildren();
  for (const e of list) {
    const r = historyRow(e);
    const li = document.createElement("li");
    li.className = "hist";
    const head = document.createElement("div");
    head.className = "hist-head";
    head.textContent = `${r.icon} ${r.machine} — ${r.outcome}`;
    const meta = document.createElement("div");
    meta.className = "muted";
    meta.textContent = `${r.scope}${r.reason ? " · " + r.reason : ""} · ${r.when}`;
    li.append(head, meta);
    ul.appendChild(li);
  }
}

/**
 * Self-heal the push subscription on open (Leg 7 — push-wake reliability): silently
 * re-subscribe if the OS dropped it, re-register if it rotated, or surface a clear re-enable
 * prompt if permission was revoked. Best-effort; never blocks the UI. Status is reflected so a
 * silently-recovered subscription reads "on" and a revoked one tells the operator how to fix it.
 */
function reconcilePush(): void {
  void syncPushSubscription((action) => {
    setText(
      "push-status",
      action === "prompt-reenable"
        ? "🔕 Notifications were turned off — tap Enable notifications to re-enable."
        : "Enable notifications so a request can wake your phone.",
    );
  }).then(({ action, ok }) => {
    if (action === "subscribe" || action === "resubscribe") {
      // Only claim "on" when the heal ACTUALLY registered with the relay — a decided-but-
      // failed heal (relay down, subscribe refused) must not read as a live wake channel.
      setText(
        "push-status",
        ok
          ? "🔔 Notifications on."
          : "⚠️ Couldn't restore notifications (relay unreachable?) — will retry on next open.",
      );
    }
    // "noop" leaves whatever status was there; an already-valid sub reads "on" from before.
  });
}

let polling = false;
let acting = false; // a decision is in flight: never reload under it
let shownRequestId: string | null = null; // the request the card currently shows
let updater: ReturnType<typeof installUpdater> | null = null;

/** Safe to reload: no request card on screen and no decision in flight. */
function canReload(): boolean {
  const card = document.getElementById("request");
  return !acting && (!card || card.style.display === "none");
}

async function poll(): Promise<void> {
  if (polling) return;
  polling = true;
  try {
    const relay = localStorage.getItem("vowed-relay-url");
    if (!relay) return;
    const res = await fetch(relay + withVault("/gate/pending"));
    if (res.status === 204) return; // nothing pending
    if (!res.ok) return;
    render((await res.json()) as PendingGrant);
  } catch {
    /* offline — try again next tick */
  } finally {
    polling = false;
    updater?.flush();
  }
}

const MINT_ROWS = ["req-standing", "req-wallet-row", "req-ops-row", "req-service-row", "req-expiry-row"];

function render(req: PendingGrant): void {
  shownRequestId = req.request_id;
  const view = requestView(req);
  if (view.kind === "mint") return renderMint(req, view);
  const grant = view.kind === "grant";
  const title = view.kind === "enroll" ? "➕ New device" : view.kind === "unlock" ? "🔓 Unlock request" : "🔑 Access request";
  show("request", true);
  for (const id of MINT_ROWS) show(id, false);
  show("req-scope-row", true);
  ($("approve") as HTMLButtonElement).disabled = false;
  setText("req-title", title);
  setText("req-code", req.code);
  setText("req-machine", view.kind === "enroll"
    ? (req.descriptor.friendly_name ?? req.descriptor.gate_id ?? "(new device)")
    : req.target?.machine ?? req.target?.daemon_id ?? "(unknown machine)");
  setText("req-scope", view.scope);
  if (view.kind === "enroll") showEnrollFingerprint(req);
  // Secrets + reason rows are grant-only — noise for an unlock (no scope) or a device enrollment.
  show("req-names-row", grant);
  show("req-reason-row", grant);
  if (grant) {
    setText("req-names", req.descriptor.names.join(", "));
    setText("req-reason", req.descriptor.reason);
  }
  setText("approve", view.kind === "unlock" ? "Face ID · Unlock" : "Face ID · Approve");
  ($("approve") as HTMLButtonElement).onclick = () => act(req, true);
  ($("deny") as HTMLButtonElement).onclick = () => act(req, false);
}

/**
 * New-device card: show the fingerprint RECOMPUTED here from the new pubkey (never the relay's
 * display copy), so the operator compares what this phone will attest. Until J-3529 it was only
 * shown inside the removed manual-confirm fallback.
 */
function showEnrollFingerprint(req: PendingGrant): void {
  const pubkey = req.descriptor.new_pubkey;
  const still = () => shownRequestId === req.request_id;
  if (!pubkey) {
    setText("req-scope", "No device key in this request — deny it");
    return;
  }
  void enrollFingerprint(pubkey).then(
    (fp) => still() && setText("req-scope", `Fingerprint ${fp} — must match the new phone`),
    () => still() && setText("req-scope", "Fingerprint unreadable — deny this request"),
  );
}

/**
 * D-028a service-token card: wallet, secret names, ops, service id, expiry DATE, machine, reason,
 * pairing code — and, plainly, that this is a standing credential that survives relock. Every value
 * shown is exactly what `decide()` hashes and signs. A malformed request can only be denied.
 */
function renderMint(req: PendingGrant, view: RequestView): void {
  shownRequestId = req.request_id;
  show("request", true);
  setText("req-title", "🪪 Standing service token");
  setText("req-code", req.code);
  show("req-scope-row", false);
  let card;
  try {
    card = mintCard(req);
  } catch (e) {
    for (const id of MINT_ROWS) show(id, false);
    show("req-names-row", false);
    show("req-reason-row", false);
    setText("req-machine", (e as Error).message);
    ($("approve") as HTMLButtonElement).disabled = true;
    ($("deny") as HTMLButtonElement).onclick = () => act(req, false);
    return;
  }
  for (const id of MINT_ROWS) show(id, true);
  show("req-names-row", true);
  show("req-reason-row", true);
  setText("req-standing", `⚠️ ${card.standing}. ${view.title}`);
  setText("req-machine", `${card.machine} · session ${card.requestorSession}`);
  setText("req-wallet", card.wallet);
  setText("req-names", card.names.join(", "));
  setText("req-ops", card.ops.join(", "));
  setText("req-service", card.serviceId);
  setText("req-expiry", `${card.expiryDate} (${card.lifetimeDays} days)`);
  setText("req-reason", card.reason);
  const approve = $("approve") as HTMLButtonElement;
  approve.disabled = false;
  setText("approve", "Face ID · Mint token");
  approve.onclick = () => act(req, true);
  ($("deny") as HTMLButtonElement).onclick = () => act(req, false);
}

async function act(req: PendingGrant, approve: boolean): Promise<void> {
  acting = true;
  let keepCard = false;
  try {
    setText("req-status", approve ? "Approving…" : "Denying…");
    const outcome = await decide(req, approve);
    if (!outcome.sent) {
      // J-3529: Face ID did not pass, so nothing was signed or sent. Keep the card up so the
      // operator can retry Face ID or deny; the request stays pending on the Mac.
      keepCard = true;
      setText("req-status", "✗ " + notApprovedMessage(outcome.reason));
      if (outcome.reason === "no-passkey") void reflectFaceId();
      return;
    }
    setText("req-status", approve ? "✓ Sent" : "Denied");
    renderHistory(); // decide() logged it — reflect it in the list immediately
  } catch (e) {
    setText("req-status", "✗ " + (e as Error).message);
  } finally {
    if (keepCard) {
      acting = false;
    } else {
      setTimeout(() => {
        show("request", false);
        acting = false;
      }, 1500);
    }
  }
}

/** Reflect whether approvals on this phone have a Face ID passkey to require. */
async function reflectFaceId(): Promise<void> {
  const ready = await hasFaceId().catch(() => false);
  setText(
    "faceid-status",
    ready
      ? "✓ Face ID is set up. Every approval requires it."
      : "Face ID is not set up on this phone. Approvals are blocked until you tap Set up Face ID.",
  );
}

/**
 * The enrollment safety-number check, shown in the page (no browser dialog). Resolves true only
 * when the operator taps "Numbers match". That tap is also the user gesture Face ID runs under.
 */
function askSafetyNumber(safety: string): Promise<boolean> {
  setText("safety-number", safety);
  show("safety", true);
  const match = $("safety-match") as HTMLButtonElement;
  const differ = $("safety-differ") as HTMLButtonElement;
  return new Promise((resolve) => {
    const done = (ok: boolean) => {
      match.onclick = null;
      differ.onclick = null;
      show("safety", false);
      resolve(ok);
    };
    match.onclick = () => done(true);
    differ.onclick = () => done(false);
  });
}

/**
 * Wire the DOM + start the poll loop. Guarded behind a browser check so this module's pure
 * helpers (e.g. `requestView`) stay importable in Node tests without a DOM. Auto-runs in the
 * browser at the bottom of the module.
 */
export function boot(): void {
  // D-069 Tier-1: capture the vault-id (`v`) + relay (`r`) from the enrollment payload URL this
  // PWA was opened with, so every /gate/* call targets the correct vault gate. Runs before wiring.
  captureEnrollmentParams();

  // Installed-app enrollment-parameter delivery (2026-07-27 smoke finding): paste the enrollment
  // link (or bare vault-id) to store the vault-id + relay the URL can't deliver to an installed app.
  $("save-link").addEventListener("click", () => {
    const ok = applyEnrollmentInput((($("enroll-link") as HTMLInputElement)).value);
    if (ok) {
      // The link may also carry the relay (r=) — reflect it in the relay field.
      const relay = localStorage.getItem("vowed-relay-url");
      if (relay) (($("relay") as HTMLInputElement)).value = relay;
      (($("enroll-link") as HTMLInputElement)).value = "";
    } else {
      setText("link-status", "✗ That doesn't look like an enrollment link (or vault id) — copy the whole link `vowed gate enroll` prints.");
      return;
    }
    reflectVaultLink();
  });

  $("save-relay").addEventListener("click", () => {
    const raw = (($("relay") as HTMLInputElement)).value.trim();
    // Normalize: lowercases scheme/host + validates, so autocapitalized input
    // ("HTTPS://...Chris-270...") can't produce a "Load failed" on the next fetch.
    let normalized = raw;
    try { normalized = new URL(raw).toString().replace(/\/+$/, ""); } catch { /* leave as typed; surfaced on use */ }
    localStorage.setItem("vowed-relay-url", normalized);
    (($("relay") as HTMLInputElement)).value = normalized;
    setText("relay-status", "Saved.");
  });

  $("enroll").addEventListener("click", async () => {
    try {
      // Fail CLEAR, not with a relay 400: the relay hard-requires the ?v= vault-id (D-069) and an
      // installed app can only get one via the paste box above.
      if (!vaultId()) {
        setText("setup-status", "✗ No vault linked. Paste the enrollment link from your Mac above, tap Use enrollment link, then enroll.");
        return;
      }
      const deviceName = (prompt("Name this device (shown on the Mac + when it approves):", "iPhone") || "iPhone").trim();
      setText("setup-status", "Enrolling… run `vowed gate enroll` on your Mac now.");
      // Never reload mid-enrollment: the gate key is saved last, so a reload could leave the Mac
      // holding a key this phone never kept (J-3616, CSO finding 2).
      acting = true;
      try {
        await enroll(deviceName, askSafetyNumber);
      } finally {
        acting = false;
      }
      setText("setup-status", "✓ Enrolled.");
      await refresh();
      // Best-effort: turn on notifications right after enrolling (this click is a user gesture).
      if (await enablePush()) setText("push-status", "🔔 Notifications on.");
    } catch (e) {
      setText("setup-status", "✗ " + (e as Error).message);
    }
  });

  // J-3529: the ONLY way a Face ID passkey is created or replaced. A failed or cancelled approval
  // never does this on its own. The old passkey stays pinned unless a new one is created.
  $("setup-faceid").addEventListener("click", async () => {
    setText("faceid-status", "Setting up Face ID…");
    acting = true; // no update reload between creating the passkey and saving its id
    let r;
    try {
      r = await setUpFaceIdOnThisPhone();
    } catch {
      r = { ok: false, reason: "failed" } as const;
    } finally {
      acting = false;
    }
    if (r.ok) {
      await reflectFaceId();
      return;
    }
    const had = await hasFaceId().catch(() => false);
    setText("faceid-status", faceIdSetupMessage(r.reason, had));
  });

  $("enable-push").addEventListener("click", async () => {
    setText("push-status", "Enabling…");
    const ok = await enablePush();
    setText("push-status", ok
      ? "🔔 Notifications on."
      : "Couldn't enable — needs the installed Home-Screen app + permission.");
  });

  // Re-enroll / add-machine affordance: an enrolled gate can still initiate a fresh enrollment
  // (e.g. pair another Mac, or re-pair after a key rotation) — surfaces the setup card again.
  $("re-enroll")?.addEventListener("click", () => {
    show("setup", true);
    setText("setup-status", "Run `vowed gate enroll` on the Mac you want to pair, then Enroll.");
  });

  // Poll immediately when the app becomes visible (e.g. opened from a notification tap),
  // rather than waiting up to 3s for the next interval. Also re-reconcile push: a subscription
  // the OS dropped while we were backgrounded (Focus Mode / restart) gets silently restored.
  // J-3616: show the running version and keep the installed app on the deployed one.
  updater = installUpdater({ canReload, setStatus: (t) => setText("update-status", t) });
  void updater.check(false);

  document.addEventListener("visibilitychange", async () => {
    if (document.hidden) return;
    poll();
    void updater?.check(false);
    if (await isEnrolled()) reconcilePush();
  });
  setInterval(poll, 3000);
  refresh();
}

if (typeof document !== "undefined") boot();
